import * as analytic from '@anm/analytic';
import { SwitchHandler } from '@anm/components/form/Switch';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import escapeName from '@anm/helpers/string/escapeName';
import toDashCase from '@anm/helpers/string/toDashCase';
import { getUpgradeLinkProps } from '@anm/helpers/upgradeLink';
import { useCallback, useRef, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WEBINAR_TABS } from '../../../../pages/webinar';

import { CustomizationTabIndexes, EMBED_TAB, LANDING_TAB, PASSWORD_TAB } from '../../../../pages/video';
import { hostedVideoActions, hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { landingsActions, landingsSelectors } from '../../../../store/modules/landings';
import { userActions, userSelectors } from '../../../../store/modules/user';
import { selectUserEmbeds, selectUserExtraEmbeds } from '../../../../store/modules/user/selectors';
import { Webinar } from '@anm/api/modules/hosting';

type TogglerName = 'landing' | 'embed' | 'password';

type UseToggleHandlersProps<T> = {
  hostId: string;
  isWebinar?: boolean;
  wasChanged?: boolean;
  activeButton?: T;
  openTab?: (index: T) => void;
};

export type Togglers = {
  [key in TogglerName]: Toggler;
};

type Toggler = {
  status: boolean;
  handler: SwitchHandler;
  disabled?: boolean;
  inActive?: boolean;
  onClick?: () => void;
  renderTooltip?: () => JSX.Element;
};

const { selectVideoPassword, selectEmbedStatus } = hostedVideoSelectors;
const { selectLanding } = landingsSelectors;
const { selectUserHostingSettings } = userSelectors;

const useTogglers = <T extends CustomizationTabIndexes | WEBINAR_TABS>({
  hostId,
  wasChanged,
  activeButton,
  isWebinar = false,
  openTab
}: UseToggleHandlersProps<T>) => {
  const dispatch = useDispatch();
  const settings = useSelector(selectUserHostingSettings);
  const currentLanding = useSelector(selectLanding);
  const landingEmbedStatus = useSelector(selectEmbedStatus);
  const webinarEmbedStatus = (currentLanding as Webinar)?.parameters?.isEmbedded;
  const embedStatus = isWebinar ? webinarEmbedStatus : landingEmbedStatus;
  const currentPassword = useSelector(selectVideoPassword);
  const { available: embedLimit, used: embeddedCount } = useSelector(selectUserEmbeds) || {};

  const { canPurchaseEmbeds } = useSelector(selectUserExtraEmbeds) || {};

  const indexes: { [key in TogglerName]: T } = {
    landing: (isWebinar ? WEBINAR_TABS.webinar : LANDING_TAB) as T,
    embed: (isWebinar ? WEBINAR_TABS.embed : EMBED_TAB) as T,
    password: (isWebinar ? WEBINAR_TABS.access : PASSWORD_TAB) as T
  };

  const isEmbedDisabled = embedLimit === embeddedCount && !embedStatus && !canPurchaseEmbeds;

  const passwordStatus = currentPassword?.isEnabled;
  const canSetPassword = settings?.canSetPassword;

  const landingPublicStatus = currentLanding?.public;
  const landingSlug = toDashCase(escapeName()(currentLanding?.parameters?.meta?.heading || ''));

  const tabDefaultStatus: { [key: number]: boolean } = useRef({
    [indexes.embed]: false,
    [indexes.landing]: true,
    [indexes.password]: false
  }).current;

  const getInitialState = (tab: T, status?: boolean) =>
    typeof status === 'undefined' ? tabDefaultStatus[tab] : status;

  const checkToggleInActiveStatus = (tab: T) => !!(wasChanged && activeButton !== tab);

  const openRelativeTab = (tab: T) => {
    if (activeButton === tab || !openTab) return;

    openTab(tab);
  };

  const renderPermissionTooltip = () => (
    <>
      To set password for your videos, please{' '}
      <a {...getUpgradeLinkProps('/pricing/upgrade?period=annual&plan=Business', '_self')}>upgrade</a>
    </>
  );

  const handleLandingStatusChange = useCallback(
    ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        landingsActions.saveLanding({
          hostId,
          slug: landingSlug,
          public: checked,
          parameters: currentLanding?.parameters
        })
      );
      openRelativeTab(indexes.landing);
    },
    [activeButton, currentLanding?.parameters]
  );

  const handlePasswordStatusChange = useCallback(() => {
    !currentPassword?.isEnabled ? analytic.trackVideoPasswordOn() : analytic.trackVideoPasswordOff();

    dispatch(
      hostedVideoActions.saveVideoPassword({
        hostId,
        security: {
          ...currentPassword,
          isEnabled: !currentPassword?.isEnabled
        }
      })
    );
    openRelativeTab(indexes.password);
  }, [currentPassword, activeButton]);

  const handleEmbedStatusChange = useCallback(
    ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      if (!isWebinar) {
        dispatch(hostedVideoActions.toggleEmbedStatus(checked));
      } else {
        dispatch(
          landingsActions.updateLanding({
            parameters: { ...currentLanding?.parameters, isEmbedded: checked }
          })
        );
      }

      if (checked) {
        dispatch(userActions.incrementEmbedStats());
        analytic.trackActivateEmbedToggle();
      } else {
        dispatch(userActions.decrementEmbedStats());
        analytic.trackDeactivateEmbedToggle();
      }
      openRelativeTab(indexes.embed);
    },
    [activeButton, embedStatus, isWebinar]
  );

  const togglers: Togglers = {
    embed: {
      status: getInitialState(indexes.embed, embedStatus),
      handler: handleEmbedStatusChange,
      inActive: checkToggleInActiveStatus(indexes.embed),
      disabled: isEmbedDisabled
    },
    landing: {
      status: getInitialState(indexes.landing, landingPublicStatus),
      handler: handleLandingStatusChange,
      inActive: checkToggleInActiveStatus(indexes.landing)
    },
    password: {
      status: getInitialState(indexes.password, passwordStatus),
      handler: handlePasswordStatusChange,
      disabled: !canSetPassword,
      inActive: checkToggleInActiveStatus(indexes.password),
      renderTooltip: !canSetPassword ? renderPermissionTooltip : undefined,
      ...(!canSetPassword && !isMobileOrIPad() && { onClick: () => openRelativeTab(indexes.password) })
    }
  };

  return [togglers];
};

export default useTogglers;
